import React, {useMemo, useState} from 'react'
import moment from 'moment'
// @ts-ignore
import ModalImage from 'react-modal-image'
import { Row, useSortBy, useTable } from 'react-table'
import { RootState } from '../../store'
import { Table } from 'react-bootstrap'
import { IEvent } from '../../store/events/interfaces'
import { IRegistration } from '../../store/registrations/interfaces'
import { getFilteredEvents } from '../../store/events/selectors'
import { EventAge } from './EventAge'
import { EventOccupation } from './EventOccupation'
import { EventActions } from './EventListActions'
import { getEventsByWeek } from '../../functions/events/eventsByWeek'
import { IProgram } from '../../store/program/interfaces'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAppSelector } from '../../hooks'
import { WorkflowStatusBadge } from '../shared/WorkflowStatusBadge'
import { InfoBadges, PriceBadge } from '../shared/Badges'

// Define the data structure of the list columns and set them
interface ColData {
  col1: number
  col2: JSX.Element
  col3: string
  col4: JSX.Element
  col5: JSX.Element
  col6: JSX.Element
  col7: JSX.Element
}

const buildColsPerWeek = (
  events: IEvent[],
  program: IProgram,
  registrations: IRegistration[],
  baseUrl: string,
) => {
  const dataPerWeek: ColData[] = []
  events.forEach((event: IEvent) => {
    dataPerWeek.push({
      col1: event.relative_id,
      col2: (
          <div className='float-right'>
              {event.cover_photo && program.enable_preview_images ? (
                  <ModalImage
                      small={event.cover_photo.thumbnail}
                      large={event.cover_photo.medium}
                      alt={
                          event.cover_photo.caption
                              ? event.cover_photo.caption
                              : `Vorschaubild der Veranstaltung ${event.name}`
                      }
                  />
              ) : (
                  <></>
              )}
          </div>
      ),
      col3: event.duration_summary,
      col4: EventAge({
        event: event,
        isFamilyRequired: program.requires_family_data,
      }),
      col5: (
        <>
            <div className='row '>
                <div>
                    <strong>
                      {event.name}
                    </strong>
                    <br/>
                    <PriceBadge event={event}/>
                    <WorkflowStatusBadge event={event}/>
                </div>
            </div>
            <div className="row">

              <InfoBadges event={event} tooltipPlacement={"top"}/>
            </div>
        </>
      ),
      col6: <EventOccupation event={event} isModal={false} />,
      col7: (
        <EventActions
          event={event}
        />
      ),
    })
  })
  return dataPerWeek
}

// Enter the event data into the required datastructure for react-table
// Also make a list of required week headers
const getTableData = (
  sortedEventsByWeek: [string, IEvent[]][],
  weekHeader: { [key: string]: JSX.Element },
  maxDate: string | { [p: string]: IEvent[] },
  program: IProgram,
  registrations: IRegistration[],
  baseUrl: string,
) => {
  let colsEndResult: ColData[] = []
  let eventCounter = 0
  sortedEventsByWeek.forEach((val, key) => {
    const [weekId, events] = val
    const weekStart = moment(weekId, 'YYYY;WW')
    weekHeader[eventCounter] = (
      <tr key={`week-tr-{key}`}>
        <td className='bg-light text-center font-weight-bold' colSpan={7}>
          {weekId === maxDate
            ? 'Veranstaltungen ohne Zeitraum'
            : `KW ${weekStart.isoWeek()} vom ${weekStart.format(
              'DD.MM',
            )} bis zum ${weekStart.days(7).format('DD.MM')}`}
        </td>
      </tr>
    )
    eventCounter += events.length
    colsEndResult = colsEndResult.concat(
      buildColsPerWeek(events, program, registrations, baseUrl),
    )
  })
  return colsEndResult
}

// Define the column headers
const setColStructure = (
  program: IProgram,
  timeSortFunc: Function,
  ageSortFunc: Function,
  nameSortFunc: Function,
  occupationSortFunc: Function,
) => {
  const ageColName = program.requires_family_data
    ? 'Altersempfehlung'
    : 'Altersbeschränkung'
  const colStructure = [
    {
      Header: 'Nr.',
      accessor: 'col1', // accessor is the "key" in the data
    },
    {
      Header: ' ',
      accessor: 'col2',
      disableSortBy: true,
    },
    {
      Header: 'Zeitraum',
      accessor: 'col3',
      sortType: timeSortFunc,
    },
    {
      Header: ageColName,
      accessor: 'col4',
      sortType: ageSortFunc,
    },
    {
      Header: 'Name',
      accessor: 'col5',
      sortType: nameSortFunc,
    },
    {
      Header: 'Aktionen',
      accessor: 'col7',
      disableSortBy: true,
    },
  ]
  if (
    program.booking_visibility !== 'never' &&
    !program.hide_event_occupation &&
    program.has_external_registration !== "1_YES"
  ) {
    colStructure.splice(-1, 0, {
      Header: 'Belegung',
      accessor: 'col6',
      sortType: occupationSortFunc,
    })
  }
  return colStructure
}

export const EventListTable = () => {
  const { data: program } = useAppSelector((state: RootState) => state.program)
  const { list: registrations } = useAppSelector(
    (state: RootState) => state.registrations,
  )
  const events = useAppSelector(getFilteredEvents)
  const { baseUrl } = useAppSelector((state: RootState) => state.misc)

  const [weekHeader, setWeekHeader] = useState({} as { [key: string]: JSX.Element })

  // get the events by week and sort the result
  const [eventsByWeek, maxDate] = getEventsByWeek(events)

  const sortedEventsByWeek = Object.entries(eventsByWeek)
    .filter(val => val[1].length > 0)
    .sort((valA, valB) => valA[0].localeCompare(valB[0]))

  const tableData = useMemo(() => getTableData(
    sortedEventsByWeek,
    weekHeader,
    maxDate,
    program,
    registrations,
    baseUrl,
    // otherwise it'll do too many nested updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [])

  const eventsByRelativeId: { [key: number]: IEvent } = events.reduce(
    (eventsById, curr) => ({ ...eventsById, [curr.relative_id]: curr }),
    {},
  )

  // Custom Sort functions
  const timeSortFunc: Function = React.useMemo(
    () => (rowA: Row, rowB: Row) => {
      const rowAEvent = eventsByRelativeId[rowA.values.col1]
      const rowBEvent = eventsByRelativeId[rowB.values.col1]
      const rowAEventStart = Date.parse(rowAEvent.start)
      const rowBEventStart = Date.parse(rowBEvent.start)

      if (rowBEventStart < rowAEventStart) return 1
      if (rowAEventStart < rowBEventStart) return -1
      return 0
    },
    [eventsByRelativeId],
  )

const ageSortFunc: Function = React.useMemo(
    () => (rowA: Row, rowB: Row) => {
      const rowAEvent = eventsByRelativeId[rowA.values.col1]
      const rowBEvent = eventsByRelativeId[rowB.values.col1]
      const minAgeA = rowAEvent.min_age ? rowAEvent.min_age : 0
      const minAgeB = rowBEvent.min_age ? rowBEvent.min_age : 0
      const maxAgeA = rowAEvent.max_age ? rowAEvent.max_age : 0
      const maxAgeB = rowBEvent.max_age ? rowBEvent.max_age : 0

      /* We sort for four Groups of Age Possibilities:
    1. without Age Restrictions
    2. with only max Age
    3. with only min Age
    4. with both restrictions
    */

      // Sort Events without min and max Age
      if (!minAgeA && !maxAgeA) {
        return 1
      } else if (!minAgeB && !minAgeB) {
        return -1
      }

      // Sort Events with only max Age
      if (!minAgeA && maxAgeA) {
        if (maxAgeA > maxAgeB) {
          return 1
        } else if(maxAgeA < maxAgeB){
          return -1
        }
      }

      if (!minAgeB && maxAgeB) {
        if (maxAgeB > maxAgeA) {
          return 1
        } else if(maxAgeB < maxAgeA){
          return -1
        }
      }

      // Sort Events with only min Age
      if (minAgeA && !maxAgeA && minAgeB && !maxAgeB) {
        if (minAgeA >= minAgeB) {
          return 1
        } else {
          return -1
        }
      }

      // Sort Events with both min and max Age
      if (minAgeA && maxAgeA && minAgeB && maxAgeB) {
        if (minAgeA >= minAgeB) {
          return 1
        } else {
          return -1
        }
      }

      // Move events with both min and max value down Part 1
      if (!minAgeA && maxAgeA && minAgeB && maxAgeB) {
        return 1
      } else if (!minAgeB && maxAgeB && minAgeA && maxAgeA) {
        return -1
      }

      // Move events with both min and max value down Part 2
      if (minAgeA && !maxAgeA && minAgeB && maxAgeB) {
        return 1
      } else if (minAgeB && !maxAgeB && minAgeA && maxAgeA) {
        return -1
      }
    },
    [eventsByRelativeId],
  )

  const occupationSortFunc: Function = React.useMemo(
    () => (rowA: Row, rowB: Row) => {
      const rowAEvent = eventsByRelativeId[rowA.values.col1]
      const rowBEvent = eventsByRelativeId[rowB.values.col1]

      if (!rowAEvent.max_participants) {
        return 1
      } else if (!rowBEvent.max_participants) {
        return -1
      }
      if (rowAEvent.registration_statistics.has_too_many_registrations) {
        return -1
      } else if (rowBEvent.registration_statistics.has_too_many_registrations) {
        return 1
      }
      const rowAOpenSlots =
        rowAEvent.max_participants -
        rowAEvent.registration_statistics.effectively_occupied_places_amount
      const rowBOpenSlots =
        rowBEvent.max_participants -
        rowBEvent.registration_statistics.effectively_occupied_places_amount
      if (rowAOpenSlots >= rowBOpenSlots) {
        return 1
      } else {
        return -1
      }
    },
    [eventsByRelativeId],
  )

  const nameSortFunc: Function = React.useMemo(
    () => (rowA: Row, rowB: Row) => {
      const rowAEventName = eventsByRelativeId[
        rowA.values.col1
        ].name.toLowerCase()
      const rowBEventName = eventsByRelativeId[
        rowB.values.col1
        ].name.toLowerCase()
      if (rowAEventName < rowBEventName) {
        return -1
      }
      if (rowAEventName > rowBEventName) {
        return 1
      }
      return 0
    },
    [eventsByRelativeId],
  )

  const columns: any = useMemo(() => setColStructure(
    program,
    timeSortFunc,
    ageSortFunc,
    nameSortFunc,
    occupationSortFunc,
    // otherwise it'll do infinite nested updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [])

  // Build the table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns: columns,
      data: tableData,
      autoResetHiddenColumns: true,
    },
    useSortBy,
  )

  let isSorted = false
  for (const column of headerGroups[0].headers) {
    //@ts-ignore isSorted doesn't exist in type
    if (column.isSorted) {
      isSorted = true
      break
    }
  }

  return (
    <Table {...getTableProps()} responsive className='mt-3'>
      <thead>
        {headerGroups.map((headerGroup, tableRowKey: number) => (
          <tr
                {...headerGroup.getHeaderGroupProps({ key: `thead-tr-${tableRowKey}` })}
          >
            {headerGroup.headers.map((column: any, tableHeaderKey: number) => (
              <th
                {...column.getHeaderProps({ key: `thead-th-${tableHeaderKey}`, ...column.getSortByToggleProps() })}
                className={'col-md-auto'}
              >
                <div className='row'>
                  <div className='d-flex col-md-auto justify-content-center'>
                    {column.render('Header')}
                    <>&nbsp;</>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <FontAwesomeIcon
                          icon='sort-down'
                          aria-label='Absteigend sortiert'
                          className='table-sort-icons'
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon='sort-up'
                          aria-label='Aufsteigend sortiert'
                          className='table-sort-icons'
                        />
                      )
                    ) : column.Header !== 'Aktionen' && column.Header !== ' ' ? (
                      <FontAwesomeIcon
                        icon='sort'
                        aria-label='unsortiert'
                        className='table-sort-icons'
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, rowKey: number) => {
          prepareRow(row)
          const weekHeaderElement = isSorted ? <></> : weekHeader[rowKey]
          return (
            <React.Fragment key={`tbody-tr-${rowKey}`}>
              {weekHeaderElement}
              <tr
                {...row.getRowProps()}
                id={`event-${tableData[rowKey].col1}`}
                tabIndex={0}
                className={`events-table-row ${rowKey % 2 !== 0 ? 'even-row' : ''}`}
              >
                {row.cells.map((cell, cellKey: number) => {
                  return (
                    <td className={"cell-column-"+cell.column.id} {...cell.getCellProps({ key: `tbody-td-${cellKey}` })}>{cell.render('Cell')}</td>
                  )
                })}
              </tr>
            </React.Fragment>
          )
        })}
      </tbody>
    </Table>
)
}
